import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import type { GridKey, Project } from '../api/types'
import {
  useAiTranslate,
  useBulkDeprecateKeys,
  useBulkTag,
  useDeleteProject,
  useGrid,
  useLocales,
  useMarkReviewed,
  usePatchKey,
  useProjects,
  usePutTranslation,
  useRetranslateCell,
  useTags,
} from '../api/hooks'
import { BulkBar } from '../editor/BulkBar'
import {
  AddKeyDialog,
  DescriptionDialog,
  ExportDialog,
  ImportDialog,
  NewProjectDialog,
  RenameKeyDialog,
  TagManagerDialog,
} from '../editor/Dialogs'
import { EditorGrid, type GridActions } from '../editor/Grid'
import { HistoryDrawer } from '../editor/HistoryDrawer'
import { LeftRail } from '../editor/LeftRail'
import { coverageByLocale } from '../lib/coverage'
import { labelMap } from '../lib/locales'
import { sortKeys } from '../lib/sort'
import { filterableStatuses, keyMatchesStatus } from '../lib/status'
import { useUi } from '../store/ui'

type DialogName =
  | 'add-key'
  | 'import'
  | 'export'
  | 'description'
  | 'new-project'
  | 'tag-manager'
  | null

const EMPTY: string[] = []

export function Editor() {
  const params = useParams<{ platform: string; project: string }>()
  const platform = params.platform ?? ''
  const projectSlug = params.project ?? 'default'
  const navigate = useNavigate()

  const projects = useProjects(platform)
  const locales = useLocales()
  const deleteProject = useDeleteProject(platform)
  const [dialog, setDialog] = useState<DialogName>(null)
  const [searchParams, setSearchParams] = useSearchParams()

  const project = useMemo(() => {
    const list = projects.data ?? []
    if (projectSlug === 'default') return list.find((item) => item.is_default)
    return list.find((item) => item.slug === projectSlug) ?? list.find((item) => item.is_default)
  }, [projects.data, projectSlug])

  // Deleting the project currently open must not leave the editor sitting on
  // a dead project — bounce to the default one for this platform.
  const handleDeleteProject = (target: Project) => {
    const warning =
      `Delete "${target.name}" (${target.slug})? Its own overridden wording stops being ` +
      "served immediately — its apps fall back to the default project's wording right away. " +
      'Nothing is erased: translations and history are kept, and you can restore it anytime ' +
      'from Settings › Client projects.'
    if (!window.confirm(warning)) return
    deleteProject.mutate(target.id, {
      onSuccess: () => {
        if (project?.id === target.id) navigate(`/p/${platform}/default`)
      },
    })
  }

  const showDeprecated = useUi((state) => state.showDeprecated)
  const setShowDeprecated = useUi((state) => state.setShowDeprecated)
  const grid = useGrid(platform, project?.id, showDeprecated)
  const put = usePutTranslation(platform, project, showDeprecated)
  const patchKey = usePatchKey(platform, project?.id)
  const retranslate = useRetranslateCell(project?.id)
  const aiTranslate = useAiTranslate(platform, project?.id)
  const markReviewed = useMarkReviewed(platform, project?.id)
  const tags = useTags(platform)
  const bulkTag = useBulkTag(platform, project?.id)
  const bulkDeprecate = useBulkDeprecateKeys(platform, project?.id)
  const [renameTarget, setRenameTarget] = useState<GridKey | null>(null)

  const clearSelectionOnNav = useUi((state) => state.clearSelection)
  const clearExpandedOnNav = useUi((state) => state.clearExpanded)
  // `selected` and `expanded` are flat, global-keyed maps (not scoped per
  // platform/project the way hiddenLocales is), so a selection made on one
  // project would otherwise silently survive a navigation to another and
  // get submitted against it. A bulk action is inherently about the rows
  // currently on screen, so reset both the moment the screen changes rather
  // than only on the BulkBar's manual "Clear selection" button. This is
  // belt-and-suspenders: every bulk handler below also intersects the
  // selection with the keys actually loaded before it ever fires a request.
  useEffect(() => {
    clearSelectionOnNav()
    clearExpandedOnNav()
  }, [platform, project?.id, clearSelectionOnNav, clearExpandedOnNav])

  const search = useUi((state) => state.search)
  const tag = useUi((state) => state.tag)
  const status = useUi((state) => state.status)
  const sort = useUi((state) => state.sort)
  const resetFilters = useUi((state) => state.resetFilters)
  // Select the record, not a derived array: a fresh array per call would make
  // zustand's snapshot comparison loop.
  const hiddenByPlatform = useUi((state) => state.hiddenLocales)
  const hiddenLocales = useMemo(
    () => hiddenByPlatform[platform] ?? EMPTY,
    [hiddenByPlatform, platform],
  )
  const pushToast = useUi((state) => state.pushToast)

  // Locales are scoped per platform: web ships English + both German variants,
  // while ios/android additionally carry es-ES/ru-RU/pl-PL/ro-RO. The grid
  // response is the authority on which apply here — using the global registry
  // instead would offer web four columns it has no locales for, and invite an
  // editor to type Spanish that /pull would then refuse as out of scope.
  const enabledLocales = useMemo(() => {
    const scoped = grid.data?.locales
    const all = (locales.data ?? []).filter((locale) => locale.enabled)
    if (!scoped) return all
    return scoped
      .map((code) => all.find((locale) => locale.code === code))
      .filter((locale): locale is NonNullable<typeof locale> => Boolean(locale))
  }, [locales.data, grid.data?.locales])
  const visibleLocales = useMemo(
    () => enabledLocales.filter((locale) => !hiddenLocales.includes(locale.code)),
    [enabledLocales, hiddenLocales],
  )
  const labels = useMemo(() => labelMap(enabledLocales), [enabledLocales])
  const isClient = Boolean(project && !project.is_default)

  // The authoritative tag list (GET /api/platforms/{slug}/tags), not derived
  // from whatever grid rows happen to be loaded: a tag that only lives on a
  // filtered-out or unloaded row must still show up in the filter dropdown.
  const allTags = useMemo(() => (tags.data ?? []).map((row) => row.tag), [tags.data])

  const visibleCodes = useMemo(() => visibleLocales.map((locale) => locale.code), [visibleLocales])

  // Per-locale progress for the project actually open, computed from the grid
  // that is already loaded — the dashboard's numbers are per platform and do
  // not know about client fallbacks.
  const coverage = useMemo(
    () => coverageByLocale(grid.data?.keys ?? [], visibleCodes),
    [grid.data, visibleCodes],
  )
  const statuses = useMemo(() => filterableStatuses(grid.data), [grid.data])

  const rows = useMemo(() => {
    const keys = sortKeys(grid.data?.keys ?? [], sort, visibleCodes)
    const needle = search.trim().toLowerCase()
    return keys.filter((key) => {
      if (tag && !key.tags.includes(tag)) return false
      if (status && !keyMatchesStatus(key.cells, visibleCodes, status)) return false
      if (!needle) return true
      if (key.name.toLowerCase().includes(needle)) return true
      if ((key.context ?? '').toLowerCase().includes(needle)) return true
      return visibleCodes.some((code) =>
        (key.cells[code] ?? []).some((cell) => cell.value?.toLowerCase().includes(needle)),
      )
    })
  }, [grid.data, search, tag, status, sort, visibleCodes])

  const setCursor = useUi((state) => state.setCursor)
  const toggleLocale = useUi((state) => state.toggleLocale)

  // Jump-to-cell deep link from the Jobs page (`?key=&locale=`): unhide the
  // locale and clear filters that could hide the target row, then move the
  // cursor once it actually appears in `rows`. Consumed once, then dropped.
  useEffect(() => {
    const jumpKey = searchParams.get('key')
    if (!jumpKey || !grid.data) return
    const jumpLocale = searchParams.get('locale')
    if (jumpLocale && hiddenLocales.includes(jumpLocale)) toggleLocale(platform, jumpLocale)
    if (search || tag || status) resetFilters()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, grid.data])

  useEffect(() => {
    const jumpKey = searchParams.get('key')
    if (!jumpKey) return
    const rowIndex = rows.findIndex((row) => row.id === jumpKey)
    if (rowIndex === -1) return
    const jumpLocale = searchParams.get('locale')
    const foundCol = jumpLocale ? visibleLocales.findIndex((locale) => locale.code === jumpLocale) : 0
    setCursor({ row: rowIndex, col: foundCol === -1 ? 0 : foundCol })
    setSearchParams({}, { replace: true })
  }, [rows, visibleLocales, searchParams, setCursor, setSearchParams])

  const actions: GridActions = useMemo(
    () => ({
      save: (vars) => put.mutate(vars),
      retranslate: (vars) => retranslate.mutate(vars),
      saveContext: (vars) =>
        patchKey.mutate(
          { keyId: vars.keyId, context: vars.context },
          { onSuccess: () => pushToast('success', 'Context note saved.') },
        ),
      // English is authored on the default project; a client project inherits it.
      readOnlyLocale: (code) =>
        isClient && enabledLocales.some((locale) => locale.code === code && locale.is_source),
      openRename: (keyRow) => setRenameTarget(keyRow),
      setIsPlural: (vars) => patchKey.mutate({ keyId: vars.keyId, is_plural: vars.isPlural }),
      setDeprecated: (vars) =>
        patchKey.mutate(
          { keyId: vars.keyId, deprecated: vars.deprecated },
          {
            onSuccess: () =>
              pushToast(
                'success',
                vars.deprecated
                  ? 'Key deprecated — removed from every export.'
                  : 'Key restored.',
              ),
          },
        ),
    }),
    [put, retranslate, patchKey, pushToast, isClient, enabledLocales],
  )

  if (projects.isError || grid.isError) {
    return (
      <p role="alert" className="m-4 rounded border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-900">
        {((projects.error ?? grid.error) as Error).message}
      </p>
    )
  }

  // The grid query waits for a project id; without this the page would sit on
  // "Loading grid…" forever for a platform that has no project at all.
  if (projects.isSuccess && !project) {
    return (
      <p role="alert" className="m-4 rounded border border-amber-400 bg-amber-50 px-3 py-2 text-xs text-amber-900">
        {platform} has no project yet. Create a project for this platform to start translating.
      </p>
    )
  }

  return (
    <div className="flex h-full min-h-0">
      <LeftRail
        platform={platform}
        projects={projects.data ?? []}
        current={project}
        locales={enabledLocales}
        labels={labels}
        hidden={hiddenLocales}
        tags={allTags}
        statuses={statuses}
        showDeprecated={showDeprecated}
        onShowDeprecatedChange={setShowDeprecated}
        onNewProject={() => setDialog('new-project')}
        onDeleteProject={handleDeleteProject}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex flex-wrap items-center gap-2 border-b border-ink-200 bg-white px-3 py-2">
          <h1 className="flex items-baseline gap-1.5 text-sm font-semibold text-ink-900">
            <span className="font-mono text-xs text-ink-600">{platform}</span>
            <span aria-hidden="true" className="text-ink-500">
              /
            </span>
            {project?.name ?? '…'}
          </h1>
          {isClient && project?.slug && (
            <span
              className="chip border-teal-300 bg-teal-50 text-teal-800"
              title="Client project: cells without an override inherit the default project's wording."
            >
              client · {project.slug}
            </span>
          )}
          <div className="flex-1" />
          <button className="btn" onClick={() => setDialog('add-key')}>
            + Add key
          </button>
          <button className="btn" onClick={() => setDialog('tag-manager')}>
            Manage tags
          </button>
          <button className="btn" onClick={() => setDialog('import')}>
            Import
          </button>
          <button className="btn" onClick={() => setDialog('export')}>
            Export
          </button>
          <button
            className="btn"
            onClick={() => setDialog('description')}
            title="The free-text client context the AI reads for this project"
          >
            AI context
          </button>
        </div>

        {grid.isLoading || locales.isLoading || !project ? (
          <p className="p-6 text-xs text-ink-600">Loading grid…</p>
        ) : (
          <EditorGrid
            rows={rows}
            locales={visibleLocales}
            labels={labels}
            project={project}
            actions={actions}
            totalKeys={grid.data?.keys.length ?? 0}
            coverage={coverage}
            onClearFilters={resetFilters}
          />
        )}

        <BulkBar
          keys={rows}
          locales={visibleLocales}
          busy={
            aiTranslate.isPending || markReviewed.isPending || bulkTag.isPending ||
            bulkDeprecate.isPending
          }
          onAiTranslate={(keyIds, localeCodes) =>
            aiTranslate.mutate({ locales: localeCodes, keyIds, onlyUntranslated: false })
          }
          onMarkReviewed={(translationIds) => markReviewed.mutate(translationIds)}
          onAddTag={(keyIds, newTag) =>
            bulkTag.mutate({ tag: newTag, keyIds, action: 'add' })
          }
          onBulkDeprecate={(keyIds, deprecated) => bulkDeprecate.mutate({ keyIds, deprecated })}
        />
      </div>

      <HistoryDrawer platform={platform} projectId={project?.id} />

      {dialog === 'add-key' && (
        <AddKeyDialog
          platform={platform}
          projectId={project?.id}
          locales={enabledLocales}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === 'new-project' && (
        <NewProjectDialog platform={platform} onClose={() => setDialog(null)} />
      )}
      {dialog === 'description' && project && (
        <DescriptionDialog platform={platform} project={project} onClose={() => setDialog(null)} />
      )}
      {dialog === 'import' && project && (
        <ImportDialog
          platform={platform}
          project={project}
          locales={enabledLocales}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === 'export' && project && (
        <ExportDialog
          platform={platform}
          project={project}
          locales={enabledLocales}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === 'tag-manager' && (
        <TagManagerDialog platform={platform} projectId={project?.id} onClose={() => setDialog(null)} />
      )}
      {renameTarget && (
        <RenameKeyDialog
          platform={platform}
          projectId={project?.id}
          keyId={renameTarget.id}
          currentName={renameTarget.name}
          onClose={() => setRenameTarget(null)}
        />
      )}
    </div>
  )
}
